import * as React from 'react';
import { useContext, createContext, useMemo, useState, useEffect, useRef, useLayoutEffect } from 'react';
import { jsx } from '@emotion/react';
import { CurrentAccountIdProvider, useCurrentAccountId } from '@zapier/identity';
import { _ as _objectSpread2 } from './objectSpread2-cdde903d.esm.js';
import { fetchJson } from '@zapier/toolbox-browser-fetch';
import { v4, validate } from 'uuid';
import { isObject } from 'lodash';

function _objectWithoutPropertiesLoose(source, excluded) {
  if (source == null) return {};
  var target = {};
  var sourceKeys = Object.keys(source);
  var key, i;

  for (i = 0; i < sourceKeys.length; i++) {
    key = sourceKeys[i];
    if (excluded.indexOf(key) >= 0) continue;
    target[key] = source[key];
  }

  return target;
}

function _objectWithoutProperties(source, excluded) {
  if (source == null) return {};
  var target = _objectWithoutPropertiesLoose(source, excluded);
  var key, i;

  if (Object.getOwnPropertySymbols) {
    var sourceSymbolKeys = Object.getOwnPropertySymbols(source);

    for (i = 0; i < sourceSymbolKeys.length; i++) {
      key = sourceSymbolKeys[i];
      if (excluded.indexOf(key) >= 0) continue;
      if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
      target[key] = source[key];
    }
  }

  return target;
}

function useSafeDispatch(dispatch) {
  const mounted = React.useRef(false);
  React.useLayoutEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  return React.useCallback(action => mounted.current ? dispatch(action) : void 0, [dispatch]);
}

function asyncReducer(state, action) {
  switch (action.type) {
    case 'loading':
      {
        return {
          status: 'loading',
          data: undefined,
          error: undefined
        };
      }

    case 'success':
      {
        return {
          status: 'success',
          data: action.data,
          error: undefined
        };
      }

    case 'error':
      {
        return {
          status: 'error',
          data: undefined,
          error: action.error
        };
      }

    default:
      {
        return state;
      }
  }
}

function useAsync(defaultState) {
  const initialState = _objectSpread2({
    status: 'idle',
    data: undefined,
    error: undefined
  }, defaultState);

  const [{
    status,
    data,
    error
  }, unsafeDispatch] = React.useReducer(asyncReducer, initialState);
  const dispatch = useSafeDispatch(unsafeDispatch);
  const run = React.useCallback(promise => {
    dispatch({
      type: 'loading'
    });
    promise.then(d => {
      dispatch({
        type: 'success',
        data: d
      });
    }, e => {
      dispatch({
        type: 'error',
        error: e
      });
    });
  }, [dispatch]);
  const setData = React.useCallback(d => dispatch({
    type: 'success',
    data: d
  }), [dispatch]);
  const setError = React.useCallback(e => dispatch({
    type: 'error',
    error: e
  }), [dispatch]);
  const flags = React.useMemo(() => ({
    isError: status === 'error',
    isIdle: status === 'idle',
    isIdleOrLoading: status === 'idle' || status === 'loading',
    isLoading: status === 'loading',
    isSuccess: status === 'success'
  }), [status]);
  return _objectSpread2(_objectSpread2({}, flags), {}, {
    data,
    error,
    run,
    setData,
    setError,
    status
  });
}

const isResponseWithResponseText = errorResponse => {
  const response = errorResponse;
  return typeof response.status === 'number' && typeof response.statusText === 'string' && typeof response.responseText === 'string';
};

const isFixableError = errorResponse => {
  if (!errorResponse) return false;
  const fixableError = errorResponse;
  return fixableError.message !== undefined && fixableError.node_id !== undefined;
};

const isError = errorResponse => {
  if (!errorResponse) return false;
  const error = errorResponse;
  return error.message !== undefined;
};

function getErrorResponse(errorResponse) {
  try {
    if (isResponseWithResponseText(errorResponse)) {
      const {
        errors
      } = JSON.parse(errorResponse.responseText);
      return errors;
    }

    return errorResponse;
  } catch (e) {
    console.warn('cannot parse response:', e);
    return errorResponse;
  }
}
const getErrorResponseMessage = errorResponse => {
  try {
    if (isResponseWithResponseText(errorResponse)) {
      const {
        errors: [firstError]
      } = JSON.parse(errorResponse.responseText);

      if (isFixableError(firstError) || isError(firstError)) {
        return firstError.message;
      }

      return (firstError || errorResponse).toString();
    }

    return errorResponse.toString();
  } catch (e) {
    console.warn('cannot parse response:', e);
    return errorResponse.toString();
  }
};

const getAllAccounts = async () => {
  try {
    const {
      objects: accounts,
      primary_id: accountIdFromEndpoint
    } = await fetchJson(`/api/v3/accounts`);
    return {
      accounts,
      accountIdFromEndpoint
    };
  } catch (error) {
    throw new Error(getErrorResponseMessage(error));
  }
};
const getAllProfiles = async () => {
  try {
    const {
      objects: profiles,
      active_id: profileId
    } = await fetchJson(`/api/v3/profiles`);
    return {
      profiles,
      profileId
    };
  } catch (error) {
    throw new Error(getErrorResponseMessage(error));
  }
};

const useAccounts = () => {
  const _useAsync = useAsync(),
        {
    run
  } = _useAsync,
        state = _objectWithoutProperties(_useAsync, ["run"]);

  React.useEffect(() => {
    if (state.status === 'idle') {
      run(getAllAccounts());
    }
  }, [run, state.status]);
  return state;
};

const useProfiles = () => {
  const _useAsync = useAsync(),
        {
    run
  } = _useAsync,
        state = _objectWithoutProperties(_useAsync, ["run"]);

  React.useEffect(() => {
    if (state.status === 'idle') {
      run(getAllProfiles());
    }
  }, [run, state.status]);
  return state;
};

const defaultContextValue$1 = {
  canEmbedShare: false,
  canViewTeamsAndCompaniesDashboard: false,
  canShareGlobally: false,
  canSharePrivately: false,
  canSharePublicly: false,
  canShareWithTeam: false,
  currentAccount: undefined,
  currentProfile: undefined,
  error: undefined,
  isError: false,
  isLoading: true,
  isReady: false
};
const AccountAndProfileContext = /*#__PURE__*/createContext(defaultContextValue$1);

const InternalAccountAndProfileProvider = ({
  children
}) => {
  const accountsQuery = useAccounts();
  const profilesQuery = useProfiles();
  const currentAccountId = useCurrentAccountId();
  const accountId = useMemo(() => currentAccountId || 0, [currentAccountId]);
  const currentAccount = useMemo(() => {
    var _accountsQuery$data;

    return (_accountsQuery$data = accountsQuery.data) === null || _accountsQuery$data === void 0 ? void 0 : _accountsQuery$data.accounts.find(a => a.id === accountId);
  }, [accountsQuery.data, accountId]);
  const currentProfile = useMemo(() => {
    var _profilesQuery$data;

    return (_profilesQuery$data = profilesQuery.data) === null || _profilesQuery$data === void 0 ? void 0 : _profilesQuery$data.profiles.find(p => {
      var _profilesQuery$data2;

      return p.id === Number((_profilesQuery$data2 = profilesQuery.data) === null || _profilesQuery$data2 === void 0 ? void 0 : _profilesQuery$data2.profileId);
    });
  }, [profilesQuery.data]);
  const value = useMemo(() => {
    var _currentAccount$switc, _currentProfile$switc, _currentAccount$switc2, _currentAccount$switc3, _currentAccount$switc4, _currentProfile$switc2, _currentAccount$switc5, _currentProfile$switc3, _currentAccount$switc6, _currentProfile$switc4, _currentAccount$switc7, _accountsQuery$error, _profilesQuery$error;

    return {
      currentAccount,
      currentProfile,
      canEmbedShare: (currentAccount === null || currentAccount === void 0 ? void 0 : (_currentAccount$switc = currentAccount.switches) === null || _currentAccount$switc === void 0 ? void 0 : _currentAccount$switc.sharing_can_embed_share) || (currentProfile === null || currentProfile === void 0 ? void 0 : (_currentProfile$switc = currentProfile.switches) === null || _currentProfile$switc === void 0 ? void 0 : _currentProfile$switc.sharing_can_embed_share) || false,
      // this switch should only be active on the account, never on profile
      canViewTeamsAndCompaniesDashboard: (currentAccount === null || currentAccount === void 0 ? void 0 : (_currentAccount$switc2 = currentAccount.switches) === null || _currentAccount$switc2 === void 0 ? void 0 : _currentAccount$switc2.sharing_can_view_teams_and_companies_dashboard) || false,
      canShareGlobally: // `sharing_new_zt_creator` must be enabled on the account
      (currentAccount === null || currentAccount === void 0 ? void 0 : (_currentAccount$switc3 = currentAccount.switches) === null || _currentAccount$switc3 === void 0 ? void 0 : _currentAccount$switc3.sharing_new_zt_creator) && ( // AND `sharing_global_sharing_enabled` must be enabled on account or profile
      (currentAccount === null || currentAccount === void 0 ? void 0 : (_currentAccount$switc4 = currentAccount.switches) === null || _currentAccount$switc4 === void 0 ? void 0 : _currentAccount$switc4.sharing_global_sharing_enabled) || (currentProfile === null || currentProfile === void 0 ? void 0 : (_currentProfile$switc2 = currentProfile.switches) === null || _currentProfile$switc2 === void 0 ? void 0 : _currentProfile$switc2.sharing_global_sharing_enabled)) || false,
      canSharePrivately: (currentAccount === null || currentAccount === void 0 ? void 0 : (_currentAccount$switc5 = currentAccount.switches) === null || _currentAccount$switc5 === void 0 ? void 0 : _currentAccount$switc5.sharing_private_sharing_enabled) || (currentProfile === null || currentProfile === void 0 ? void 0 : (_currentProfile$switc3 = currentProfile.switches) === null || _currentProfile$switc3 === void 0 ? void 0 : _currentProfile$switc3.sharing_private_sharing_enabled) || false,
      canSharePublicly: (currentAccount === null || currentAccount === void 0 ? void 0 : (_currentAccount$switc6 = currentAccount.switches) === null || _currentAccount$switc6 === void 0 ? void 0 : _currentAccount$switc6.sharing_public_sharing_enabled) || (currentProfile === null || currentProfile === void 0 ? void 0 : (_currentProfile$switc4 = currentProfile.switches) === null || _currentProfile$switc4 === void 0 ? void 0 : _currentProfile$switc4.sharing_public_sharing_enabled) || false,
      // this switch should only be active on the account, never on profile
      canShareWithTeam: currentAccount !== null && currentAccount !== void 0 && (_currentAccount$switc7 = currentAccount.switches) !== null && _currentAccount$switc7 !== void 0 && _currentAccount$switc7.sharing_team_sharing_enabled && currentAccount !== null && currentAccount !== void 0 && currentAccount.is_team_account ? true : false,
      isLoading: accountsQuery.isIdleOrLoading || profilesQuery.isIdleOrLoading,
      isError: accountsQuery.isError || profilesQuery.isError,
      isReady: accountsQuery.isSuccess && profilesQuery.isSuccess && currentAccount !== undefined && currentProfile !== undefined,
      error: ((_accountsQuery$error = accountsQuery.error) === null || _accountsQuery$error === void 0 ? void 0 : _accountsQuery$error.toString()) || ((_profilesQuery$error = profilesQuery.error) === null || _profilesQuery$error === void 0 ? void 0 : _profilesQuery$error.toString()) || undefined
    };
  }, [accountsQuery.error, accountsQuery.isError, accountsQuery.isIdleOrLoading, accountsQuery.isSuccess, currentAccount, currentProfile, profilesQuery.error, profilesQuery.isError, profilesQuery.isIdleOrLoading, profilesQuery.isSuccess]);
  return jsx(AccountAndProfileContext.Provider, {
    value: value
  }, children);
};

const AccountAndProfileProvider = ({
  children
}) => jsx(CurrentAccountIdProvider, null, jsx(InternalAccountAndProfileProvider, null, children));
const useAccountAndProfile = () => {
  const context = useContext(AccountAndProfileContext);
  return context;
};

const isBrowser = () => typeof window !== 'undefined';

const parseReadValue = value => {
  try {
    return JSON.parse(value);
  } catch (e) {
    console.warn(`Could not parse value read from sessionStorage.`, e);
  }

  return value;
};

const readValue = key => {
  if (isBrowser()) {
    try {
      return window.sessionStorage.getItem(key);
    } catch (e) {
      console.warn(`Could not read "${key}" from sessionStorage.`, e);
    }
  }

  return undefined;
};

const stringifyWriteValue = value => {
  try {
    return value && isObject(value) ? JSON.stringify(value) : value;
  } catch (e) {
    console.warn(`Could not stringify value before writing it to sessionStorage.`, e);
  }

  return value;
};

const writeValue = (key, value) => {
  if (isBrowser()) {
    try {
      window.sessionStorage.setItem(key, value);
    } catch (e) {
      console.warn(`Could not write "${key}" to sessionStorage.`, e);
    }
  }
};

const useSessionStorage = key => {
  // Use `useState` to prevent `sessionStorage` from being
  // accessed on every rerun of this hook.
  const [value, setValue] = useState(() => {
    const rawValue = readValue(key);
    return rawValue ? parseReadValue(rawValue) : rawValue;
  });
  useEffect(() => {
    const rawValue = readValue(key);
    setValue(rawValue ? parseReadValue(rawValue) : rawValue);
  }, [key]);
  useEffect(() => {
    const stringifiedValue = stringifyWriteValue(value);
    writeValue(key, stringifiedValue);
  }, [key, value]);
  return [value, setValue];
};

const SHARING_TRACKING_SESSION_ID = '_z_sharing_tracking_session_id';
function useSessionId() {
  const sessionIdRef = React.useRef(v4()); // tracking needs an id that won't change

  const [sessionId, setSessionId] = useSessionStorage(SHARING_TRACKING_SESSION_ID);
  React.useLayoutEffect(() => {
    let isValid = false;

    try {
      const result = validate(sessionId);
      isValid = result;
    } catch (e) {
      console.warn('uuidValidate failed. Details: ', e);
    }

    if (!sessionId || !isValid) {
      setSessionId(sessionIdRef.current);
    }
  }, [sessionId, setSessionId]);
  let isValid = false;

  try {
    const result = validate(sessionId);
    isValid = result;
  } catch (e) {
    console.warn('uuidValidate failed. Details: ', e);
  }

  return isValid ? sessionId : sessionIdRef.current;
}

function useFetch(uri, init) {
  const mounted = useRef(false);
  useLayoutEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  const [status, setStatus] = useState('idle');
  const [data, setData] = useState();
  const [error, setError] = useState();
  useEffect(() => {
    if (uri) {
      setStatus('loading');
      fetchJson(uri, init).then(result => {
        if (mounted.current) {
          setData(result);
          setStatus('resolved');
        }
      }).catch(e => {
        console.error(e);

        if (mounted.current) {
          setError(e.message);
          setStatus('rejected');
        }
      });
    }
  }, [init, uri]);
  return {
    status,
    data,
    error
  };
}

const useAccount = ({
  accountId,
  skip
}) => {
  const account = useFetch(!accountId || skip ? undefined : `/api/v4/accounts/${accountId}/`);
  const loading = useMemo(() => account.status === 'idle' || account.status === 'loading', [account.status]);
  const data = useMemo(() => account.data === undefined ? undefined : {
    account: {
      id: account.data.id,
      isTeamAccount: account.data.is_team_account,
      normalizedName: account.data.name,
      normalizedImageUrl: account.data.logo_url
    }
  }, [account.data]);
  return {
    loading,
    data
  };
};

const useTeammates = ({
  accountId,
  skip
}) => {
  const teammates = useFetch(!accountId || skip ? undefined : `/api/v4/accounts/${accountId}/members/?limit=500`);
  const loading = useMemo(() => teammates.status === 'idle' || teammates.status === 'loading', [teammates.status]);
  const data = useMemo(() => teammates.data === undefined ? undefined : {
    account: {
      members: {
        edges: teammates.data.results.filter(tm => tm.id !== accountId).map(tm => ({
          description: tm.email,
          email: tm.email,
          emailHash: tm.email_hash,
          id: tm.id.toString(),
          imageUrl: tm.photo_url,
          name: tm.full_name,
          recordId: tm.id,
          role: tm.role
        }))
      }
    }
  }, [accountId, teammates.data]);
  return {
    loading,
    data
  };
};

const defaultContextValue = {
  teamAccount: undefined,
  isLoading: false,
  teammates: []
};
const TeamSharingContext = /*#__PURE__*/createContext(defaultContextValue);
const TeamSharingProvider = ({
  children
}) => {
  const {
    currentAccount,
    canShareWithTeam
  } = useAccountAndProfile();
  const accountId = useMemo(() => (currentAccount === null || currentAccount === void 0 ? void 0 : currentAccount.id) || 0, [currentAccount]);
  const skip = useMemo(() => !accountId || !canShareWithTeam, [accountId, canShareWithTeam]);
  const accountQuery = useAccount({
    accountId,
    skip
  });
  const teammatesQuery = useTeammates({
    accountId,
    skip
  });
  const value = useMemo(() => {
    var _accountQuery$data, _accountQuery$data$ac, _teammatesQuery$data, _teammatesQuery$data$, _teammatesQuery$data$2;

    return {
      isLoading: accountQuery.loading || teammatesQuery.loading,
      teamAccount: accountQuery.loading || ((_accountQuery$data = accountQuery.data) === null || _accountQuery$data === void 0 ? void 0 : (_accountQuery$data$ac = _accountQuery$data.account) === null || _accountQuery$data$ac === void 0 ? void 0 : _accountQuery$data$ac.isTeamAccount) !== true ? undefined : accountQuery.data.account,
      teammates: teammatesQuery.loading || !((_teammatesQuery$data = teammatesQuery.data) !== null && _teammatesQuery$data !== void 0 && (_teammatesQuery$data$ = _teammatesQuery$data.account) !== null && _teammatesQuery$data$ !== void 0 && (_teammatesQuery$data$2 = _teammatesQuery$data$.members) !== null && _teammatesQuery$data$2 !== void 0 && _teammatesQuery$data$2.edges.length) ? [] : teammatesQuery.data.account.members.edges
    };
  }, [accountQuery.data, accountQuery.loading, teammatesQuery.data, teammatesQuery.loading]);
  return jsx(TeamSharingContext.Provider, {
    value: value
  }, children);
};
const useTeamSharing = () => {
  const context = useContext(TeamSharingContext);
  return context;
};

const ZapSharingProvider = ({
  children
}) => jsx(AccountAndProfileProvider, null, jsx(TeamSharingProvider, null, children));

const useZapSharingData = () => {
  const accountAndProfile = useAccountAndProfile();
  const sessionId = useSessionId();
  const teamSharing = useTeamSharing();
  return {
    accountAndProfile,
    sessionId,
    teamSharing
  };
};

export { AccountAndProfileProvider as A, ZapSharingProvider as Z, _objectWithoutProperties as _, useAccountAndProfile as a, getErrorResponse as b, useTeamSharing as c, useZapSharingData as d, useFetch as e, getErrorResponseMessage as g, isBrowser as i, useAsync as u };
